// `Charts` -- three chart panes, and the one dashboard that is not interactive.
//
// All three are over open barriers only, which is the API's behaviour and not a
// parameter: the archive is how a closed barrier is reached, and a chart that
// silently included six years of closed ones would answer a different question
// at ten times the magnitude.

import { Histogram } from '../charts/Histogram';
import { ErrorNote, Screen } from '../shell/Screen';
import { useAsync } from '../data';
import * as api from '../api';
import { actionCategory } from '../format';

export function ChartsScreen() {
  const byAction = useAsync(() => api.barriersByAction(), []);
  const byCommodity = useAsync(() => api.barriersByCommodity(), []);
  const byJob = useAsync(() => api.barriersByJob(), []);

  const loading = byAction.loading || byCommodity.loading || byJob.loading;
  const error = byAction.error ?? byCommodity.error ?? byJob.error;

  return (
    <Screen
      title="Charts"
      // Counts are what the API sends and percent is what the source labels
      // with, so the subtitle has to say which of the two a reader is looking
      // at. It said "by count" over three charts labelled in percent.
      description="Open barriers. Each bar is labelled with its share of the total; hover for the count."
      loading={loading}
    >
      {error && <ErrorNote error={error} />}
      <div className="panes charts">
        <section className="pane">
          <h2>By Action Category</h2>
          <Histogram
            bars={(byAction.data?.items ?? []).map((item) => ({
              // The chart is drawn over the decomposed triple and labelled the
              // way the source's 23 values read. `action_category_legacy` is
              // null on every row created since the load, so a chart over it
              // would show the estate freezing on the day of the migration.
              label: actionCategory({
                action_type: item.action_type,
                origin_site_name: item.origin_site_name,
                dest_site_name: item.dest_site_name,
              }),
              count: item.count,
            }))}
            total={byAction.data?.total ?? 0}
            colour="blue"
          />
        </section>

        <section className="pane">
          <h2>By Commodity</h2>
          <Histogram
            bars={(byCommodity.data?.items ?? []).map((item) => ({
              label: item.commodity_name ?? 'No commodity',
              count: item.count,
            }))}
            total={byCommodity.data?.total ?? 0}
            colour="red"
          />
        </section>

        <section className="pane wide">
          <h2>By Job</h2>
          <Histogram
            bars={(byJob.data?.items ?? []).map((item) => ({
              label: item.order_number ?? 'No job number',
              count: item.count,
            }))}
            total={byJob.data?.total ?? 0}
            colour="green"
            orientation="horizontal"
            // The job chart carries no legend in the source. Horizontal bars
            // label themselves, so there is nothing a legend would add.
            legend={false}
            top={20}
          />
        </section>
      </div>
    </Screen>
  );
}
